<template>
    <div class="members-tab">
        <div class="panel-header">
            <div class="left">
                <strong>팀원 목록</strong>
                <p class="total">총 <span>{{ filteredMemberList.length }}</span>명의 팀원이 있습니다.</p>
            </div>
            <div class="right">
                <div class="btn-area text-right">
                    <button class="btn-submit" @click="openInviteMemberModal" :disabled="!selectedCrewId">팀원 초대</button>
                    <button class="btn-submit" :disabled="state.selectedMemberIds.length === 0" @click="deleteSelectedCrewMembers">선택 내보내기</button>
                </div>
            </div>
        </div>

        <div class="search-wrapper">
            <div class="search-box">
                <input 
                    type="text" 
                    v-model.trim="state.searchKeyword" 
                    placeholder="이름 또는 이메일로 검색..." 
                    class="search-input"
                />
                <span v-if="state.searchKeyword" class="search-clear" @click="state.searchKeyword = ''">×</span>
            </div>
        </div>

        <div class="table-wrapper">
            <table class="table">
                <thead>
                    <tr>
                        <th style="width: 60px; text-align: center;">No</th>
                        <th style="width: 48px">
                            <input type="checkbox" :checked="isAllMembersChecked" @change="toggleAllMembers($event)" />
                        </th>
                        <th>이름</th>
                        <th>이메일</th>
                        <th style="width: 120px; text-align: center;">BOX 할당</th>
                        <th>역할</th>
                        <th style="width: 28px"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="state.loading.members">
                        <td colspan="7" class="empty">팀원 목록 불러오는 중...</td>
                    </tr>
                    <tr v-else-if="filteredMemberList.length === 0">
                        <td colspan="7" class="empty">{{ state.searchKeyword ? '검색 결과가 없습니다.' : '팀원이 없습니다.' }}</td>
                    </tr>
                    <tr v-for="(m, index) in filteredMemberList" :key="m[MEMBER_KEYS.id]" @click="goMemberDetail(m)" class="row-clickable">
                        <td style="text-align: center;">{{ index + 1 }}</td>
                        <td>
                            <input type="checkbox" :value="m[MEMBER_KEYS.id]" v-model="state.selectedMemberIds" @click.stop />
                        </td>
                        <td>{{ m[MEMBER_KEYS.name] }}</td>
                        <td>{{ m[MEMBER_KEYS.email] }}</td>
                        <td style="text-align: center;">{{ m.assignedProductCount || 0 }}</td>
                        <td>{{ m[MEMBER_KEYS.roleName] || '-' }}</td>
                        <td class="row-arrow">›</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- [모달] 팀원 초대 — 본문만 스크롤, 헤더/버튼은 항상 보이도록 -->
        <div v-if="state.modals.inviteMember" class="ss-modal-backdrop" @click.self="closeInviteMemberModal">
            <div class="ss-modal ss-modal--scroll-body">
                <h3>팀원 초대</h3>
                <div class="ss-modal-scroll-body">
                <h4 class="invite-section-title">단일 초대</h4>
                <div class="form">
                    <label>이름 <input v-model.trim="state.inviteForm.memberName" type="text" /></label>
                    <label>이메일 <input v-model.trim="state.inviteForm.memberEmail" type="email" /></label>
                </div>
                <h4 class="invite-section-title invite-section-title--bulk">일괄 초대</h4>
                <div class="form excel-invite-block">
                    <div class="excel-invite-row">
                        <label class="excel-file-label">엑셀 파일로 초대 (.xlsx/.xls)
                            <input ref="excelInput" type="file" accept=".xlsx,.xls" @change="onExcelSelected" />
                        </label>
                        <a
                            class="btn-excel-template"
                            :href="EXCEL_TEMPLATE_DOWNLOAD_URL"
                            target="_blank"
                            rel="noopener noreferrer"
                        >엑셀 양식 다운로드</a>
                    </div>
                    <div v-if="state.excelFile" class="file-pill">{{ state.excelFile.name }}</div>
                </div>
                <div class="excel-format-example">
                    <div class="example-title">엑셀 양식 예시</div>
                    <div class="example-table-wrapper">
                        <table class="example-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>CompanyName</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>홍길동</td>
                                    <td>hong@example.com</td>
                                    <td>회사명</td>
                                </tr>
                                <tr>
                                    <td>김철수</td>
                                    <td>kim@example.com</td>
                                    <td>회사명</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                </div>
                <div class="actions">
                    <button class="btn-submit" @click="submitInviteMember" :disabled="!canSubmitInviteMember">초대</button>
                    <button class="btn-submit" @click="closeInviteMemberModal">닫기</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'userMembersTab',
    props: {
        selectedCrewId: { type: [Number, String], required: false },
    },
    data() {
        return {
            EXCEL_TEMPLATE_DOWNLOAD_URL: 'https://www.ssflex.co.kr/api/file/download/2240',
            MEMBER_KEYS: { id: 'id', name: 'userName', email: 'email', roleName: 'roleName' },
            FORM_FILE_FIELD: 'file',
            state: {
                loading: { members: false },
                memberList: [],
                selectedMemberIds: [],
                searchKeyword: '',
                modals: { inviteMember: false },
                inviteForm: { memberName: '', memberEmail: '' },
                excelFile: null,
            },
        };
    },
    computed: {
        filteredMemberList() {
            if (!this.state.searchKeyword) {
                return this.state.memberList;
            }
            const keyword = this.state.searchKeyword.toLowerCase();
            return this.state.memberList.filter(member => {
                const name = (member[this.MEMBER_KEYS.name] || '').toLowerCase();
                const email = (member[this.MEMBER_KEYS.email] || '').toLowerCase();
                return name.includes(keyword) || email.includes(keyword);
            });
        },
        isAllMembersChecked() {
            return this.filteredMemberList.length > 0 && this.state.selectedMemberIds.length === this.filteredMemberList.length;
        },
        canSubmitInviteMember() {
            const hasTextForm = this.state.inviteForm.memberName && this.state.inviteForm.memberEmail;
            const hasExcel = !!this.state.excelFile;
            return !!this.selectedCrewId && (hasTextForm || hasExcel);
        },
    },
    watch: {
        selectedCrewId: {
            immediate: true,
            handler() { this.fetchCrewMembers(); },
        },
    },
    methods: {
        goMemberDetail(member) {
            const crewId = this.selectedCrewId;
            const memberId = member?.[this.MEMBER_KEYS.id];
            if (!crewId || !memberId) return;
            this.$router.push({ name: 'crewMemberDetail', params: { crewId, memberId } });
        },
        fetchCrewMembers() {
            if (!this.selectedCrewId) { this.state.memberList = []; this.state.searchKeyword = ''; return; }
            this.state.loading.members = true;
            this.state.selectedMemberIds = [];
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getCrewMemberList',
                { crewId: this.selectedCrewId },
                (result) => {
                    try { this.state.memberList = this.getSafeArray(result); }
                    finally { this.state.loading.members = false; }
                }
            );
        },
        toggleAllMembers(event) {
            const checked = event.target.checked;
            if (checked) {
                this.state.selectedMemberIds = this.filteredMemberList.map(m => m[this.MEMBER_KEYS.id]);
            } else {
                this.state.selectedMemberIds = [];
            }
        },
        deleteSelectedCrewMembers() {
            if (!this.selectedCrewId || this.state.selectedMemberIds.length === 0) return;
            const params = { crewId: this.selectedCrewId, list: this.commaJoin(this.state.selectedMemberIds) };
            if (!confirm('선택한 팀원을 내보낼까요?')) return;
            this.axiosCaller.delete(
                this,
                this.APIs.LICENSE + '/deleteCrewMembers',
                params,
                () => this.fetchCrewMembers()
            );
        },
        // 초대 모달
        openInviteMemberModal() {
            this.state.modals.inviteMember = true;
            this.state.inviteForm.memberName = '';
            this.state.inviteForm.memberEmail = '';
            this.state.excelFile = null;
            this.$refs?.excelInput && (this.$refs.excelInput.value = '');
        },
        closeInviteMemberModal() { this.state.modals.inviteMember = false; },
        onExcelSelected(e) { const f = e?.target?.files?.[0]; this.state.excelFile = f || null; },
        submitInviteMember() {
            if (!this.selectedCrewId) return;
            if (this.state.excelFile) {
                const fd = new FormData();
                fd.append('crewId', this.selectedCrewId);
                fd.append(this.FORM_FILE_FIELD, this.state.excelFile);
                this.axiosCaller.post(this, this.APIs.LICENSE + '/inviteCrewMembers', fd, () => {
                    this.closeInviteMemberModal();
                    this.fetchCrewMembers();
                });
                return;
            }
            if (!this.state.inviteForm.memberName || !this.state.inviteForm.memberEmail) return;
            const payload = { crewId: this.selectedCrewId, memberName: this.state.inviteForm.memberName, memberEmail: this.state.inviteForm.memberEmail };
            this.axiosCaller.post(this, this.APIs.LICENSE + '/inviteCrewMember', payload, () => {
                this.closeInviteMemberModal();
                this.fetchCrewMembers();
            });
        },
        // 공통 유틸
        getData(resOrData) { return resOrData?.data ?? resOrData; },
        getSafeArray(resOrData) {
            const result = this.getData(resOrData);
            if (result?.status && result.status !== 'ok') {
                this.gritterErr(result?.msg || '요청이 실패했습니다.');
                return [];
            }
            const topCandidates = [result?.list, result?.rows, result?.content, result?.data];
            for (const v of topCandidates) if (Array.isArray(v)) return v;
            const nestedCandidates = [result?.data?.list, result?.data?.rows, result?.data?.content];
            for (const v of nestedCandidates) if (Array.isArray(v)) return v;
            if (result && typeof result === 'object') {
                for (const k of Object.keys(result)) if (Array.isArray(result[k])) return result[k];
            }
            return [];
        },
        commaJoin(ids) { return Array.isArray(ids) ? ids.join(',') : String(ids ?? ''); },
        gritterErr(msg) { const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.'; this.swalUtils?.gritter ? this.swalUtils.gritter('error', m, 'error', 2000) : console.error('[ERR]', m); },
    },
};
</script>

<style scoped>
.invite-section-title {
    margin: 12px 0 8px;
    font-size: 14px;
    font-weight: 600;
    color: #444;
}

.invite-section-title--bulk {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e5e5e5;
}

.search-wrapper {
    margin-bottom: 16px;
}

.search-box {
    position: relative;
    max-width: 400px;
}

.search-input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}

.search-input:focus {
    outline: none;
    border-color: #007bff;
}

.search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    font-size: 20px;
    color: #999;
    line-height: 1;
    padding: 0 4px;
}

.search-clear:hover {
    color: #333;
}

.excel-invite-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}

.excel-file-label {
    flex: 1;
    min-width: 200px;
}

.btn-excel-template {
    display: inline-block;
    padding: 8px 14px;
    font-size: 14px;
    border: 1px solid #007bff;
    color: #007bff;
    border-radius: 4px;
    text-decoration: none;
    white-space: nowrap;
    box-sizing: border-box;
}

.btn-excel-template:hover {
    background: #007bff;
    color: #fff;
}
</style>


